import { motion } from 'framer-motion';
import { Target, Users, TrendingUp, Award } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: <Target size={40} />,
      title: 'Our Mission',
      description: 'To strengthen your brand\'s presence across Instagram, Facebook, LinkedIn, and YouTube, building both brand awareness and lead generation funnels that work for B2B and B2C audiences alike.',
    },
    {
      icon: <TrendingUp size={40} />,
      title: 'Strategic Marketing',
      description: 'Developing data-driven strategies to help businesses achieve their marketing goals effectively with measurable results and ROI.',
    },
    {
      icon: <Users size={40} />,
      title: 'Client-Centric Approach',
      description: 'We prioritize understanding your unique business needs and crafting customized solutions that deliver real growth and engagement.',
    },
    {
      icon: <Award size={40} />,
      title: 'Performance-Driven',
      description: 'Focusing on measurable results and ROI to ensure your marketing efforts deliver real business growth and tangible outcomes.',
    },
  ];

  const industries = [
    'Fashion & Retail',
    'Travel & Tourism',
    'Education & Coaching',
    'Real Estate',
    'Startups & Technology',
    'Healthcare & Wellness',
    'Automotive',
    'Food & Beverage',
  ];

  return (
    <div className="min-h-screen pt-32 pb-20" data-testid="about-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-20"
        >
          <h1 className="text-5xl md:text-6xl font-bold mb-6 gradient-text" data-testid="about-title">
            About Optive Digital
          </h1>
          <p className="text-gray-400 text-lg max-w-4xl mx-auto leading-relaxed">
            A modern digital marketing agency helping businesses grow online with creative design, powerful advertising strategies, and engaging social media content.
          </p>
        </motion.div>

        {/* Story Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-32"
        >
          <div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Digital Excellence Meets Luxury</h2>
            <p className="text-gray-400 leading-relaxed mb-6">
              Optive Digital is built on the foundation of powerful visual storytelling, strategic marketing, and performance-driven campaigns. We understand that in today's digital landscape, your brand needs more than just presence—it needs impact.
            </p>
            <p className="text-gray-400 leading-relaxed mb-6">
              Our team specializes in creating comprehensive digital marketing solutions that elevate your brand across all major social media platforms. From Instagram and Facebook to LinkedIn and YouTube, we craft strategies that resonate with your target audience and drive measurable results.
            </p>
            <p className="text-gray-400 leading-relaxed">
              Based in Karnal, Haryana, we serve businesses across India and beyond, helping startups, SMBs, and established brands achieve their digital marketing goals through creativity, consistency, and data-driven strategy.
            </p>
          </div>
          <div className="relative">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/2304d325-e856-415f-87c3-0487b81fdef4/images/75c5bbea7279e1dfac7acc12947150c8bd3d5ca9e87d47a1a5ee8933d49943ee.png"
              alt="Team Collaboration"
              className="rounded-2xl shadow-2xl"
            />
          </div>
        </motion.div>

        {/* Values Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-32"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center gradient-text">Our Core Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass rounded-2xl p-8 hover:border-white/20 transition-all"
                data-testid={`value-${index}`}
              >
                <div className="text-[#3B82F6] mb-4">{value.icon}</div>
                <h3 className="text-xl font-semibold mb-3 text-white">{value.title}</h3>
                <p className="text-gray-400 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Industries Served */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass rounded-2xl p-12 text-center"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6 gradient-text">Industries We Serve</h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Our expertise spans across multiple industries, delivering tailored digital marketing solutions
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {industries.map((industry, index) => (
              <span
                key={index}
                className="bg-white/5 border border-white/10 px-6 py-3 rounded-full text-gray-300 hover:border-[#3B82F6] hover:text-[#3B82F6] transition-all"
                data-testid={`industry-${index}`}
              >
                {industry}
              </span>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-20 text-center"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6 gradient-text">
            Ready to Work With Us?
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Let's discuss how we can help your brand grow
          </p>
          <a
            href="/contact"
            data-testid="about-cta-button"
            className="inline-block bg-gradient-to-r from-[#3B82F6] to-[#2563EB] text-white px-8 py-4 rounded-full font-semibold hover:shadow-[0_0_30px_rgba(59,130,246,0.6)] transition-all hover:scale-105"
          >
            Get in Touch
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default About;